import React from "react";
import cns from "classnames";
import {
  Grid,
  Paper,
  Divider,
  Typography,
  makeStyles,
  Link,
  IconButton,
  Chip
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import RemoveIcon from "@material-ui/icons/Delete";
import VerifiedUserIcon from "@material-ui/icons/VerifiedUser";
import LocalFloristIcon from "@material-ui/icons/LocalFlorist";

import locale from "@bonliva-crm/locale";
import { ThemeType } from "@theme";
import { Title, WithTooltip } from "@components";
import { AccommodationCardDetailsFragment } from "@graphql";
import ErrorIcon from "@material-ui/icons/PhoneLocked";
import { lighten } from "polished";

export type Props = {
  style?: React.CSSProperties;
  data: AccommodationCardDetailsFragment;
  highlightSearch?: string;
  fullInfo?: boolean;
  onClick?: (e: React.SyntheticEvent<any>) => any;
  onEdit?: (e: React.SyntheticEvent<any>) => any;
  onDelete?: (e: React.SyntheticEvent<any>) => any;
};

export const AccommodationCard = (props: Props) => {
  const classes = useStyles({});
  const { data, onClick, onEdit, onDelete, fullInfo = false } = props;

  const rootClasses = cns(classes.root, {
    [classes.clickable]: Boolean(props.onClick)
  });

  const pic = data?.ImgUrl ? (
    <img className={classes.img} src={data.ImgUrl} alt="thumbnail" />
  ) : (
    <div className={classes.img} />
  );
  const siteHref = `//${data.Site.toLowerCase().replace(/https:\/\/|http:\/\//gi, "")}`;
  const error = data.unfit?.value;

  function getEnvCertTitle(name?: string) {
    return `${locale.EnvCertification}${name ? `: ${name}` : ""}`;
  }

  return (
    <Paper
      onClick={onClick}
      className={rootClasses}
      style={props.style}
      data-test="AccommodationCard"
    >
      <Grid
        container
        spacing={1}
        className={cns(classes.mainInfo, error && classes.error)}
      >
        <Grid item xs={12} sm={2} style={{ paddingRight: 8 }}>
          {pic}
        </Grid>

        <Grid item xs={12} sm={7} alignItems="flex-start">
          <Title className={`${classes.name} highlighter`} variant="h6" gutterBottom>
            {data.Name}
            <Typography variant="caption" className={classes.caption} display={"inline"}>
              {locale.accommodationType[data.Type]}
            </Typography>
            <div className={classes.titleIcons}>
              {data.envCertification && (
                <WithTooltip title={getEnvCertTitle(data.envCertificationName)}>
                  <LocalFloristIcon className={classes.titleIcon} color="secondary" />
                </WithTooltip>
              )}
              {data.preferredChoice && (
                <WithTooltip title={locale.PreferredChoice}>
                  <VerifiedUserIcon className={classes.titleIcon} color="secondary" />
                </WithTooltip>
              )}
            </div>
            {!!error && <Chip icon={<ErrorIcon />} label={locale.UnfitConsultant} />}
          </Title>

          <div onClick={(e) => e.stopPropagation()} className={classes.inlineBlock}>
            <Link variant={"body2"} component="button" color="textSecondary">
              {locale.ShowOnMap}
            </Link>
            <Typography variant={"body2"} color="textSecondary">
              {data.Email?.join(", ")}
            </Typography>
            <Link
              variant={"body2"}
              href={siteHref}
              target={"_blank"}
              color="textSecondary"
            >
              {data.Site}
            </Link>
            <Typography variant={"body2"}>{data.Phone?.join(", ")}</Typography>
          </div>
        </Grid>
        <Grid
          item
          xs={12}
          sm={3}
          direction={"row-reverse"}
          container
          alignItems={"center"}
          style={{ height: "40px" }}
        >
          {fullInfo && (
            <>
              {onDelete && (
                <IconButton onClick={onDelete} size={"small"}>
                  <RemoveIcon />
                </IconButton>
              )}
              {onEdit && (
                <IconButton onClick={onEdit} size={"small"}>
                  <EditIcon />
                </IconButton>
              )}
            </>
          )}
          <Typography variant={"body2"} align={"right"}>
            {data.ResponsiblePersonRef &&
              `${locale.Responsible}: ${data.ResponsiblePersonRef?.Name}`}
          </Typography>
        </Grid>
      </Grid>

      <Divider />

      {data.Offers?.map((o, i) => {
        return (
          <React.Fragment key={i}>
            <div className={classes.info} onClick={(e) => e.stopPropagation()}>
              <Typography className={classes.name}>
                <b>{o.Name}</b>
                <span className={classes.caption}>
                  {o.Price} {locale.accommodationPriceType[o.PriceType]}
                </span>
              </Typography>

              <Typography className={classes.name}>
                {locale.Rooms}:&nbsp;<span>{o.Rooms}</span>,&nbsp;
                {locale.Beds}:&nbsp;<span>{o.Beds}</span>
              </Typography>

              <div className={classes.options}>
                <Typography color="primary" variant={"body1"}>
                  {o.Area} kvm{" "}
                </Typography>

                {o.Options?.map((x, ind) => (
                  <div className={classes.option} key={ind}>
                    <Chip
                      label={locale.accommodationOfferOptions[x]}
                      variant="outlined"
                    />
                  </div>
                ))}
              </div>
            </div>
            {i + 1 < data.Offers.length && <Divider />}
          </React.Fragment>
        );
      })}
      {fullInfo && (
        <>
          <Divider />
          <div className={classes.info}>
            <Typography gutterBottom>
              <b>{locale.CheckIn}: </b>
              {data?.CheckIn}
            </Typography>
            <Typography gutterBottom>
              <b>{locale.CheckOut}: </b>
              {data?.CheckOut}
            </Typography>
            <Typography gutterBottom>
              <b>{locale.Instruction}: </b>
              {data?.Instruction}
            </Typography>
            <Typography gutterBottom>
              <b>{locale.CancellationPolicy}: </b>
              {data?.CancellationPolicy}
            </Typography>
            {data?.Site && (
              <Typography gutterBottom>
                <b>{locale.Site}: </b>
                <Link href={siteHref} target="_blank">
                  {data.Site}
                </Link>
              </Typography>
            )}
          </div>
        </>
      )}
    </Paper>
  );
};

const useStyles = makeStyles((theme: ThemeType) => ({
  root: {},

  clickable: {
    cursor: "pointer",
    "&:hover, &:active": {
      boxShadow: theme.shadows[4]
    }
  },

  mainInfo: {
    margin: 0,
    width: "100%",
    padding: 12
  },

  name: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap"
  },

  img: {
    display: "block",
    maxWidth: "155px",
    width: "100%",
    height: "105px",
    objectFit: "cover",
    margin: "0 auto",
    background: "#eee"
  },

  caption: {
    padding: "0 10px"
  },

  titleIcons: {
    display: "flex",
    alignItems: "center"
  },

  titleIcon: {
    marginRight: "5px"
  },

  price: {
    padding: "0 10px"
  },

  info: {
    ...theme.mixins.childrenMarginBottom(8),
    padding: 16
  },

  inlineBlock: {
    display: "inline-block"
  },

  options: {
    // ...theme.mixins.childrenMarginRight(4),
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap"
  },

  option: {
    display: "inline-block",
    padding: 2
  },

  error: {
    backgroundColor: lighten(0.15, theme.palette.primary.main)
  }
}));
